/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { ExtensibleFunction } from '../models';
import { getNumberFormatter, NumberFormats } from '../number-format';
import { Currency } from '../query';

interface CurrencyFormatterConfig {
  d3Format?: string;
  currency: Currency;
  locale?: string;
}

interface CurrencyFormatter {
  (value: number | null | undefined): string;
}

export const getCurrencySymbol = (currency: Partial<Currency>) => {
  try {
    return (
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.symbol,
      })
        .formatToParts(1)
        .find(x => x.type === 'currency')?.value || currency.symbol
    );
  } catch (e) {
    return currency.symbol;
  }
};

class CurrencyFormatter extends ExtensibleFunction {
  d3Format: string;

  locale: string;

  currency: Currency;

  constructor(config: CurrencyFormatterConfig) {
    // 先提取配置，在闭包中直接使用，避免 this 绑定问题
    const { currency } = config;
    const d3Format = config.d3Format || NumberFormats.SMART_NUMBER;
    const locale = config.locale || 'en-US';

    // 使用闭包捕获配置，确保在各种回调中能正确访问
    const normalizedD3Format = d3Format.replace(/\$/g, '');
    const capturedCurrency = currency;
    super((value: number | null | undefined) => {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return `${value}`;
      }
      const formattedValue = getNumberFormatter(normalizedD3Format)(value);
      if (!capturedCurrency?.symbol) {
        return formattedValue as string;
      }
      if (capturedCurrency.symbolPosition === 'prefix') {
        return `${getCurrencySymbol(capturedCurrency)} ${formattedValue}`;
      }
      return `${formattedValue} ${getCurrencySymbol(capturedCurrency)}`;
    });

    this.d3Format = d3Format;
    this.currency = currency;
    this.locale = locale;
  }

  hasValidCurrency() {
    return Boolean(this.currency?.symbol);
  }

  getNormalizedD3Format() {
    return this.d3Format.replace(/\$/g, '');
  }

  format(value: number) {
    // 直接调用实例自身（因为实例是一个函数）
    return (this as any)(value);
  }
}

export default CurrencyFormatter;
